import { useCallback } from 'react'
import { useShallow } from 'zustand/react/shallow'

import { useCADPageStore } from '@/state/cad'
import { Step } from '@/services/queries/operation_steps/types'
import { GLTFObject } from '@/lib/cad/GLTFObject'

export const useHighlightStep = (gltf?: GLTFObject) => {
  const highlightColor = useCADPageStore((state) => state.highlightColor)
  const setCadPageState = useCADPageStore(useShallow((state) => state.setState))

  const highlightStep = useCallback(
    (step?: Step, selectedColor?: number) => {
      const savedColor = useCADPageStore.getState().highlightColor
      const color =
        selectedColor !== undefined ? selectedColor : (savedColor ?? 0x0080ff)

      if (highlightColor !== color) {
        setCadPageState({ highlightColor: color })
      }

      if (!step || !gltf) return

      const colorMap = useCADPageStore.getState().colorMap
      const highlightedParts = useCADPageStore.getState().highlightedParts

      // Reset previously highlighted parts that are not in the new step
      highlightedParts.forEach((partUUID) => {
        if (!step.assembly_group_ids.includes(partUUID)) {
          gltf.resetColor(partUUID)
        }
      })

      // Apply highlight color to new step parts
      step.assembly_group_ids.forEach((partUUID) => {
        gltf.setColor(partUUID, color)
      })

      const partColors = step.assembly_group_ids.reduce((acc, partUUID) => {
        acc[partUUID] = color
        return acc
      }, {})

      // Remove old highlighted parts from colorMap and add new ones
      const newColorMap = { ...colorMap }
      highlightedParts.forEach((partUUID) => {
        if (!step.assembly_group_ids.includes(partUUID)) {
          delete newColorMap[partUUID]
        }
      })
      Object.assign(newColorMap, partColors)

      setCadPageState({
        colorMap: newColorMap,
        highlightedParts: step.assembly_group_ids,
      })

      return newColorMap
    },
    [setCadPageState, gltf, highlightColor],
  )
  return highlightStep
}
